import React, { ReactNode, MouseEvent } from 'react';
import {
    FormControl,
    FormControlLabel,
    FormControlProps,
    Radio,
    RadioGroup,
    RadioGroupProps,
    Box,
} from '@mui/material';
import { useController, FieldError, Path } from 'react-hook-form';
import { FormErrorText } from '@/components/forms/other/ErrorText/FormErrorText';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useTheme } from '@mui/material';
import { Control, Controller } from 'react-hook-form';

interface CustomRadioFieldProps<T extends object> {
    control: Control<T>;
    name: Path<T>;
    data: {
        value: string | number | boolean;
        label: number | string;
        disabled?: boolean;
        customLabel?: ReactNode;
    }[];
    error?: FieldError | null;
    radioClassName?: string;
    controlClassName?: string;
    activeClassName?: string;
    onClick?: (evt: MouseEvent) => void;
    radioGroupProps?: RadioGroupProps;
}

type RadioFieldProps<T extends object> = CustomRadioFieldProps<T> &
    Omit<FormControlProps, 'error' | 'label' | 'name'>;

export const RadioField = <T extends object>(props: RadioFieldProps<T>) => {
    const { t } = usePhrase();
    const theme = useTheme();
    const {
        control,
        name,
        data,
        error,
        radioClassName,
        controlClassName,
        activeClassName,
        onClick,
        radioGroupProps,
        ...rest
    } = props;

    const {
        fieldState: { error: fieldError },
    } = useController({
        name,
        control,
    });

    const label = (d: { label: number | string; customLabel?: ReactNode }) => {
        if (d.customLabel) {
            return d.customLabel;
        } else {
            if (typeof d.label === 'string') {
                return d.label;
            } else {
                return t(d.label);
            }
        }
    };

    return (
        <>
            <Controller
                name={name}
                control={control}
                render={({ field: { onChange, value } }) => (
                    <RadioGroup {...radioGroupProps} className={radioClassName} data-testid={name}>
                        {data.length &&
                            data.map((d) => (
                                <FormControl
                                    key={String(d.value)}
                                    className={`${controlClassName ?? ''} ${
                                        String(d.value) === String(value)
                                            ? (activeClassName ?? 'active')
                                            : ''
                                    }`}
                                    {...rest}
                                >
                                    <FormControlLabel
                                        value={d.value}
                                        control={
                                            <Radio
                                                style={{
                                                    color: error
                                                        ? theme.palette.error.main
                                                        : undefined,
                                                }}
                                                onChange={() => {
                                                    onChange(d.value);
                                                }}
                                                onClick={onClick}
                                                checked={String(d.value) === String(value)}
                                            />
                                        }
                                        disabled={d?.disabled}
                                        label={label(d)}
                                    />
                                </FormControl>
                            ))}
                    </RadioGroup>
                )}
            />
            <Box sx={{ ml: 1.8 }}>
                <FormErrorText error={props.error || fieldError} />
            </Box>
        </>
    );
};
